import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  IoChevronForward,
  IoPersonOutline,
  IoCardOutline,
  IoNotificationsOutline,
  IoWalletOutline,
  IoDocumentTextOutline,
  IoCalculatorOutline,
  IoMegaphoneOutline,
  IoHelpCircleOutline,
  IoLogOutOutline,
  IoPersonRemoveOutline,
} from 'react-icons/io5';
import { useAuth } from '../../../context/AuthContext';
import { getRealtorProfile } from '../../../services/userProfileService';
import { openChannelTalkWorkflow } from '../../../services/channelTalkService';
import ConfirmModal from '../../../components/common/ConfirmModal';
import AlertModal from '../../../components/common/AlertModal';

const MENU_SECTIONS = [
  {
    title: '계정 관리',
    items: [
      { label: '회원정보 수정', path: '/realtor/mypage/edit', icon: IoPersonOutline },
      { label: '계좌인증', path: '/realtor/mypage/account', alert: true, icon: IoCardOutline },
      { label: '알림 설정', path: '/realtor/mypage/notification', icon: IoNotificationsOutline },
    ],
  },
  {
    title: '수익 관리',
    items: [
      { label: '출금 내역', path: '/realtor/mypage/withdraw', icon: IoWalletOutline },
      { label: '리워드 상세 내역', path: '/realtor/mypage/commission', icon: IoDocumentTextOutline },
      { label: '정산 안내', path: '/realtor/mypage/stats', icon: IoCalculatorOutline },
    ],
  },
  {
    title: '고객센터',
    items: [
      { label: '채팅상담 (채널톡)', action: 'channelTalk', icon: IoHelpCircleOutline },
      { label: '공지사항', path: '/realtor/mypage/notice', icon: IoMegaphoneOutline },
      { label: 'FAQ', path: '/realtor/mypage/faq', icon: IoHelpCircleOutline },
    ],
  },
  {
    title: '기타',
    items: [
      { label: '로그아웃', action: 'logout', icon: IoLogOutOutline },
      { label: '회원 탈퇴', action: 'withdraw', icon: IoPersonRemoveOutline },
    ],
  },
];

const Profile = () => {
  const navigate = useNavigate();
  const { signOut, userData } = useAuth();

  const [logoutOpen, setLogoutOpen] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [bizName, setBizName] = useState('');
  const userName = userData?.name || '사용자';
  const initial = userName.slice(0, 2);
  const photoURL = userData?.photoURL || '';
  const businessName = bizName || '이음테스트';

  useEffect(() => {
    if (!userData?.uid) return;
    getRealtorProfile(userData.uid).then((r) => {
      if (r?.businessName) setBizName(r.businessName);
    }).catch(() => {});
  }, [userData?.uid]);
  const referralCode = userData?.uid?.slice(0, 10) || '';

  const handleAction = async (action) => {
    if (action === 'logout') {
      setLogoutOpen(true);
    } else if (action === 'withdraw') {
      setWithdrawOpen(true);
    } else if (action === 'channelTalk') {
      openChannelTalkWorkflow('realtor');
    }
  };

  const confirmLogout = async () => {
    setLogoutOpen(false);
    await signOut();
    navigate('/login', { replace: true });
  };

  const copyCode = () => {
    navigator.clipboard?.writeText(referralCode).then(() => alert('코드가 복사되었습니다.'));
  };

  return (
    <Container>
      {/* 프로필 카드 */}
      <ProfileCard>
        <ProfileRow>
          {photoURL ? <AvatarImg src={photoURL} alt="프로필" /> : <Avatar>{initial}</Avatar>}
          <ProfileInfo>
            <UserName>{userName}</UserName>
            <BusinessName>{businessName}</BusinessName>
          </ProfileInfo>
          <EditLink onClick={() => navigate('/realtor/mypage/edit')}>프로필 변경</EditLink>
        </ProfileRow>
      </ProfileCard>

      {/* 추천인 코드 */}
      <ReferralSection>
        <ReferralRow>
          <ReferralText>추천인 코드: {referralCode}</ReferralText>
          <OutlineBtn onClick={copyCode}>코드복사</OutlineBtn>
          <OutlineBtn onClick={() => navigate('/realtor/recommend/realtor')}>가입 초대하기</OutlineBtn>
        </ReferralRow>
      </ReferralSection>

      {/* 메뉴 섹션 */}
      {MENU_SECTIONS.map((section) => (
        <MenuSection key={section.title}>
          <SectionLabel>{section.title}</SectionLabel>
          <MenuList>
            {section.items.map((item, idx) => (
                <MenuItem
                  key={item.label}
                  $last={idx === section.items.length - 1}
                  onClick={() => {
                    if (item.external) window.open(item.external, '_blank', 'noopener,noreferrer');
                    else if (item.path) navigate(item.path);
                    else handleAction(item.action);
                  }}
                >
                  <MenuLeft>
                    {item.icon && <MenuIcon><item.icon size={20} color="#6B7280" /></MenuIcon>}
                    <MenuLabel>{item.label}</MenuLabel>
                  </MenuLeft>
                  <MenuRight>
                    {item.alert && <AlertDot />}
                    <IoChevronForward size={16} color="#9CA3AF" />
                  </MenuRight>
                </MenuItem>
            ))}
          </MenuList>
        </MenuSection>
      ))}

      {logoutOpen && (
        <ConfirmModal
          type="question"
          title="로그아웃 하시겠습니까?"
          confirmText="로그아웃"
          cancelText="취소"
          onConfirm={confirmLogout}
          onCancel={() => setLogoutOpen(false)}
        />
      )}

      {withdrawOpen && (
        <AlertModal
          type="info"
          title="회원 탈퇴"
          message={'회원 탈퇴는 고객센터에 문의해주세요.'}
          onConfirm={() => setWithdrawOpen(false)}
        />
      )}
    </Container>
  );
};

export default Profile;

const Container = styled.div`
  padding-bottom: ${({ theme }) => theme.spacing.lg};
`;

const ProfileCard = styled.div`
  background: ${({ theme }) => theme.realtor.headerBg};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing.base} ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.base};
`;

const ProfileRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
`;

const AvatarImg = styled.img`
  width: 48px;
  height: 48px;
  border-radius: ${({ theme }) => theme.radius.full};
  object-fit: cover;
  flex-shrink: 0;
`;

const Avatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${({ theme }) => theme.radius.full};
  background: ${({ theme }) => theme.realtor.avatarBg};
  color: ${({ theme }) => theme.realtor.avatarText};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  flex-shrink: 0;
`;

const ProfileInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const UserName = styled.div`
  font-size: ${({ theme }) => theme.fontSize.xl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
`;

const BusinessName = styled.div`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textLight};
  margin-top: 2px;
`;

const EditLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.primary};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
`;

const ReferralSection = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const ReferralRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const ReferralText = styled.span`
  font-size: ${({ theme }) => theme.fontSize.base};
  color: ${({ theme }) => theme.colors.text};
  margin-right: auto;
`;

const OutlineBtn = styled.button`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border: 1px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme }) => theme.colors.bgWhite};
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  cursor: pointer;
  white-space: nowrap;
`;

const MenuSection = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.base};
`;

const SectionLabel = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const MenuList = styled.div``;

const MenuItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.base} 0;
  border-bottom: ${({ $last, theme }) => ($last ? 'none' : `1px solid ${theme.colors.borderLight}`)};
  cursor: pointer;
`;

const MenuLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
`;

const MenuIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const MenuLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ theme }) => theme.fontWeight.regular};
  color: ${({ theme }) => theme.colors.text};
`;

const MenuRight = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const AlertDot = styled.span`
  width: 18px;
  height: 18px;
  border-radius: ${({ theme }) => theme.radius.full};
  background: ${({ theme }) => theme.colors.danger};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  color: #fff;
  &::after {
    content: '!';
    font-weight: 700;
  }
`;
